import {
  Box,
  Center,
  Heading,
  SimpleGrid,
  GridItem,
  Grid,
  Flex,
  Stack,
  Text,
  useColorModeValue as mode,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalHeader,
  ModalBody,
  ModalContent,
  FormControl,
  Input,
  ModalCloseButton,
  FormLabel,
  ModalFooter,
  Img,
  Button,
} from "@chakra-ui/react";
import { Logo } from "components/logo";
import * as React from "react";
import { DurationSwitcher } from "./duration-switcher";
import { PricingCard } from "./pricing-card";
import { useSession } from "next-auth/react";
import { useRouter } from "next/dist/client/router";
import { firebaseClient } from "../../firebaseClient";
import * as analytics from "../../utils/analytics";
import { updateEmail, guessTimezone } from "../../controllers/settings";
import { warm } from "../../controllers/search";
import ShoutoutEmbed from 'shoutout-react'
import { setInLocalStorage } from "../../utils/helpers";
import { ModalInfoIcon } from "components/popups/ModalInfoIcon";
import { variant } from "theme/names";
import { trackWithRudder } from "utils/rudderstack";
import { getAccount } from "utils/sessionHelper";

const BillingFrequencyTypes = ["monthly", "yearly"] as const;
export type BillingFrequency = typeof BillingFrequencyTypes[number];

export const Onboard = () => {
  const router = useRouter();
  const { data: session, status } = useSession() ?? {}
  const loading = status === "loading"


  const [email, setEmail] = React.useState("");
  const [billingFrequency, setBillingFrequency] = React.useState<BillingFrequency>("monthly");
  const { isOpen, onOpen, onClose } = useDisclosure()
  const [isLoading, setIsLoading] = React.useState(false);
  const initialRef = React.useRef<HTMLInputElement>(null)

  React.useEffect(() => {

    if (session?.user?.uid) {
      analytics.init(session);
      if (session?.user?.data?.subscription?.isSubscribed) {
        router.push("/search");
      }
      else if (session?.user?.data && (!session?.user?.data?.isNeedPay || !session?.user?.data?.onboardPage)) {
        analytics.log("load_onboard", { isNeedPay: true }, session.user.uid);
        analytics.log("signup", { onboardPage: "onboard" });

        //rudder stuff
        if (typeof window !== undefined && getAccount(session)?.id) {
          const { name, id, email, dateSignUp, subStatus, subscription } = getAccount(session);
          //@ts-ignore
          trackWithRudder(window?.rudderanalytics, { name, id, email, dateSignUp, subStatus, subscription }, 'sign_up_taplio', undefined, window?.isDevRudder);
        }

        const db = firebaseClient.firestore();
        db.collection("users")
          .doc(session.user.uid)
          .update({ isNeedPay: true, onboardPage: "onboard" }).catch((e) => {
            console.log("e: " + e.message);
          });
        session.user.data.isNeedPay = true;
      }
    }
  }, [session]);

  React.useEffect(() => {

    let isSkipped = (localStorage.getItem('isSkippedEmailPopup') == "true");
    console.log("isSkipped: " + isSkipped);
    if (!isSkipped)
      onOpen();

  }, []);

  React.useEffect(() => {

    if (!email && session?.user?.data?.email)
      //@ts-ignore
      setEmail(session?.user?.data?.email);

    guessTimezone(session);

  }, [session]);

  const skipPopup = () => {
    onClose();
    setInLocalStorage('isSkippedEmailPopup', "true");
  }

  return (
    <Box as="section" bg={mode("gray.50", "gray.800")} py="10">
      <Box
        maxW={{ base: "xl", md: "7xl" }}
        mx="auto"
        px={{ base: "6", md: "8" }}
      >
        <Center mb={4}>
          <Box>
            <Logo />
          </Box>
        </Center>

        <Text
          mt="4"
          maxW="xl"
          mx="auto"
          fontSize="xl"
          color={mode("gray.600", "gray.400")}
          textAlign="center"
        >
          Gain consistency and grow your audience
        </Text>

        <Grid
          mt={10}
          // templateRows="repeat(2, 1fr)"
          // templateColumns="repeat(6, 1fr)"
          templateColumns={{
            lg: "repeat(6, 1fr)",
            md: "repeat(1, 1fr)",
            sm: "repeat(1, 1fr)",
          }}
          // templateColumns={{sm: 6, md: 6}}
          columnGap={{ lg: 10, md: 0 }}
          rowGap={5}
        >
          <GridItem colSpan={4}>
            <Stack spacing={4}>
              {[
                {
                  title: "Find high performing posts on any topic",
                  desc: "Whether you post about marketing, entrepreneurship, finance or the car industry, you’ll find amazing and high-performing posts to take inspiration from. Improve your posts and start growing your audience like a professional copywriter.",
                },
                {
                  title: "Save them to your collection",
                  desc: "Build your very own “swipe file” with the most amazing posts you find. This way you can easily visit it whenever you feel like you need help to get inspired. You can also import posts directly from Twitter and share your collection publicly.",
                },
                {
                  title: "Receive daily post inspirations in your inbox",
                  desc: "Being consistent on Twitter is key to your growth. Every day, we analyse your Twitter account with our AI engine and send you a post to help you get inspired.",
                },
                {
                  title: "Get a weekly analysis of your account and followers",
                  desc: "Every week, we run our AI engine on your account and tell you what were your most successful topics you talked about as well as the best time to post according to your follower activity. Oh and we also add thread ideas for you to plan long form content.",
                },
              ].map((e, index) => {
                return (
                  <Box
                    key={index}
                    bg={mode("gray.100", "gray.600")}
                    borderRadius="8px"
                  >
                    <Text
                      m="4"
                      fontSize="lg"
                      fontWeight="bold"
                      color={mode("gray.600", "gray.50")}
                      textAlign="left"
                    >
                      {e.title}
                    </Text>
                    <Text
                      mt="0"
                      m="4"
                      fontSize="md"
                      color={mode("gray.600", "gray.50")}
                      textAlign="left"
                    >
                      {e.desc}
                    </Text>
                  </Box>
                );
              })}
            </Stack>
          </GridItem>
          <GridItem colSpan={2}>
            <PricingCard
              onChangeBillingFrequency={() =>
                setBillingFrequency(
                  billingFrequency === "monthly" ? "yearly" : "monthly"
                )
              }
              billingFrequency={billingFrequency}
              name="Pro plan"
              description="Enjoy the full power of TweetHunter"
              price={billingFrequency === "monthly" ? 14 : 11}
              features={[
                "Unlimited search results",
                "Save posts to your collection",
                "Daily tweet inspirations in your inbox",
                "Weekly AI analysis of your account",
                "Email support",
              ]}
              //@ts-ignore
              onClickStartFreeTrial={() => {
                console.log("click start trial");
                if (session?.user?.data && !session.user.data.description) {
                  console.log("call warm");
                  warm(session);
                }
              }}
            />
          </GridItem>
        </Grid>
      </Box>
      <Box p={4} display="flex" flexDirection="column" alignItems="center">
        <Heading textAlign="center" mt={8} mb={16}>
          See what people say about us
        </Heading>
        <Box minWidth="60%" maxWidth="1200px">
          {/* <div className="shoutout-embed" data-wall="TweetHunter"></div> */}
          <ShoutoutEmbed url="TweetHunter" />
        </Box>
      </Box>
      <Box>
        <PricingCard
          onChangeBillingFrequency={() =>
            setBillingFrequency(
              billingFrequency === "monthly" ? "yearly" : "monthly"
            )
          }
          billingFrequency={billingFrequency}
          name="Pro plan"
          description="Enjoy the full power of TweetHunter"
          price={billingFrequency === "monthly" ? 14 : 11}
          features={[
            "Unlimited search results",
            "Save posts to your collection",
            "Daily tweet inspirations in your inbox",
            "Weekly AI analysis of your account",
            "Email support",
          ]}
        />
      </Box>
      <Modal
        isOpen={isOpen}
        onClose={skipPopup}
        closeOnOverlayClick={false}
        //@ts-ignore
        initialFocusRef={initialRef}
        size="xl"
      >
        <ModalOverlay bg="white" />
        <ModalContent>
          <ModalHeader>
            {/* <ModalInfoIcon /> */}
            <Box display="flex" justifyContent="space-between">
              <Text>
                {"Thanks for signing up " +
                  (session?.user?.data?.name ? session?.user?.data?.name : "")}
              </Text>
              {session?.user?.image && (
                <Img
                  w="10"
                  h="10"
                  rounded="3xl"
                  objectFit="cover"
                  src={session?.user?.image}
                  alt="Chakra UI"
                  mr={2}
                />
              )}
            </Box>
          </ModalHeader>
          <ModalBody pb={6}>
            <FormControl
              as="form"
              onSubmit={(e) => {
                e.preventDefault();
              }}
            >
              <FormLabel pb={3} pl={1} fontSize="md" fontWeight="400">
                You’re a couple steps away from unlocking the power of Tweet
                Hunter and <b>becoming a tweet machine.</b>
              </FormLabel>
              <FormLabel pb={3} pl={1} fontSize="md" fontWeight="400">
                We use email to send you{" "}
                <b>personalized tweet recommendations</b> and perform an{" "}
                <b>AI-powered analysis of your Twitter account.</b>
              </FormLabel>
              <FormLabel pb={5} pl={1} fontSize="md" fontWeight="400">
                <b>
                  Could you please confirm this email is the one you usually
                  check?
                </b>{" "}
                Or change it to your usual email.
              </FormLabel>
              <Input
                type="email"
                ref={initialRef}
                placeholder="Your email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </FormControl>
          </ModalBody>

          <ModalFooter mb={2}>
            {/* <Button onClick={skipPopup} mr={3}>Skip</Button> */}
            <Button
              onClick={async () => {
                setIsLoading(true);
                updateEmail(session?.user?.uid, email);
                skipPopup();
                setIsLoading(false);
              }}
              // colorScheme="blue"
              isLoading={isLoading}
              variant={variant.Button.primary}
            >
              Save
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};
